// src/components/ChatDialog.js - Updated for IndexedDB compatibility
import React, { useState, useEffect, useRef } from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter';
// eslint-disable-next-line no-unused-vars
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Anthropic } from "@anthropic-ai/sdk";
import { decryptData } from "../encryptionUtils";
import FileUpload from './FileUpload';
import SaveIcon from './SaveIcon';
import SummaryIcon from './SummaryIcon';
import PromptEnhancement from './PromptEnhancement';
import MagicWand from './MagicWand';
import PromptLibraryButton from './PromptLibraryButton';
import LibraryIcon from './LibraryIcon';
import SavePromptModal from './SavePromptModal';
import PromptLibraryModal from './PromptLibraryModal';
import FreeTrialManager from './FreeTrialManager';
import TrialStatus from './TrialStatus';
import MessageFileAttachments from './MessageFileAttachments';
import AttachedFilesPreview from './AttachedFilesPreview'; 
import Spinner from './Spinner';
import ApiKeyRequiredModal from './ApiKeyRequiredModal';
import ContinuationIndicator from './ContinuationIndicator';
import { cleanFilesForStorage } from '../fileUtils/cleanFileUtils';
import './FileAttachments.css';
import { saveCodeToFile } from '../saveCodeUtils';
import { generateEnhancementPrompt } from '../enhancementUtils';
import ChatTour from './ChatTour';
import CustomHelpButton from './CustomHelpButton';
import './ChatDialog.css';
import './ChatTour.css';
import { prepareFilesForAnthropicApi, getPdfSupportedModel, needsPdfBetaHeader } from '../fileUtils/pdfUtils';

// Create a custom style based on docco with VS Code-like colors
const customCodeStyle = {
  // Base styles
  'hljs': {
    display: 'block',
    overflowX: 'auto',
    padding: '1em',
    background: '#1e1e1e',
    color: '#d4d4d4'
  },
  // Comments - light green
  'hljs-comment': { 
    color: '#6A9955',
    fontStyle: 'italic'
  },
  // Keywords - purple/blue
  'hljs-keyword': { 
    color: '#569CD6',
    fontWeight: '500'
  },
  // Control keywords - purple
  'hljs-built_in': { 
    color: '#569CD6' 
  },
  // Strings - orange/red
  'hljs-string': { 
    color: '#CE9178' 
  },
  // Numbers - light blue
  'hljs-number': { 
    color: '#B5CEA8' 
  },
  // Function names - yellow
  'hljs-function': { 
    color: '#DCDCAA' 
  },
  'hljs-title': { 
    color: '#DCDCAA'
  },
  'hljs-title.function': {
    color: '#DCDCAA'
  },
  // Function params
  'hljs-params': { 
    color: '#9CDCFE'
  },
  // Variables and object properties - light blue
  'hljs-variable': { 
    color: '#9CDCFE'
  },
  'hljs-variable.constant': {
    color: '#4FC1FF'
  },
  'hljs-attr': { 
    color: '#9CDCFE'
  },
  'hljs-property': {
    color: '#9CDCFE'
  },
  // Class names and types - teal
  'hljs-type': { 
    color: '#4EC9B0' 
  },
  'hljs-class': {
    color: '#4EC9B0'
  },
  // Literals - blue
  'hljs-literal': { 
    color: '#569CD6'
  },
  // Special characters - white
  'hljs-punctuation': {
    color: '#D4D4D4'
  },
  'hljs-operator': { 
    color: '#D4D4D4'
  },
  // Regex - gold
  'hljs-regexp': { 
    color: '#D16969'
  },
  // HTML/XML tags
  'hljs-tag': { 
    color: '#569CD6'
  },
  'hljs-name': { 
    color: '#569CD6'
  },
  // Import/export statements
  'hljs-meta': {
    color: '#569CD6'
  },
  // Template literals
  'hljs-template-variable': { 
    color: '#9CDCFE'
  },
  'hljs-template-tag': {
    color: '#569CD6'
  },
  // Additions in diffs 
  'hljs-addition': { 
    color: '#6A9955',
    backgroundColor: 'rgba(155, 185, 85, 0.2)'
  },
  // Deletions in diffs
  'hljs-deletion': { 
    color: '#CE9178',
    backgroundColor: 'rgba(255, 0, 0, 0.2)'
  },
  // Special highlight for special strings like 'new_file', 'vs-dark', etc.
  'hljs-symbol': {
    color: '#D7BA7D'
  },
  // JavaScript arrow functions
  'hljs-function.arrow': {
    color: '#D4D4D4'
  }
};

// Helper function to extract clean code without line numbers from code blocks
const extractCleanCode = (codeBlock) => {
  // First try to find if we have the original code stored in a data attribute
  if (codeBlock.dataset && codeBlock.dataset.originalCode) {
    return codeBlock.dataset.originalCode;
  }

  // If no data attribute is available, we need to extract the code without line numbers
  // Find all code lines, excluding the line numbers
  const codeLines = [];
  
  // Look for the actual code elements within the syntax highlighter
  const codeElements = codeBlock.querySelectorAll('.react-syntax-highlighter-line-number + span, .syntax-highlighter-code > span:not(.react-syntax-highlighter-line-number)');
  
  if (codeElements && codeElements.length > 0) {
    // Extract text from each code line
    codeElements.forEach(element => {
      codeLines.push(element.textContent);
    });
    return codeLines.join('\n');
  }
  
  // Fallback method: split by lines and remove the line numbers
  const content = codeBlock.textContent;
  const lines = content.split('\n');
  const cleanLines = [];
  
  // Process each line to remove line numbers
  for (let i = 0; i < lines.length; i++) {
    // Look for patterns like "1 " at the beginning of lines
    const line = lines[i];
    // Use regex to match line numbers at start of line - they are usually right-aligned with spaces
    const cleanLine = line.replace(/^\s*\d+\s+/, '');
    cleanLines.push(cleanLine);
  }
  
  return cleanLines.join('\n');
};

function ChatDialog({ topic, onMessageSubmit, isLoading, searchTerm, messageInputRef }) {
  const [message, setMessage] = useState('');
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [files, setFiles] = useState([]);
  const [showEnhancementDialog, setShowEnhancementDialog] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhancedPrompt, setEnhancedPrompt] = useState('');
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isSavingCode, setIsSavingCode] = useState(false);
  
  // Free trial state
  const [trialRemaining, setTrialRemaining] = useState(0);
  // Begin with trialActive as null to indicate "unknown" state, not false
  const [trialActive, setTrialActive] = useState(null);
  // Add a new state variable to track trial initialization status
  const [trialInitialized, setTrialInitialized] = useState(false);
  // Add a loading state specifically for the initial setup
  const [isInitializing, setIsInitializing] = useState(true);

  // New state variables for prompt library feature
  const [showSavePromptModal, setShowSavePromptModal] = useState(false);
  const [showPromptLibraryModal, setShowPromptLibraryModal] = useState(false);
  const [promptToSave, setPromptToSave] = useState('');
  
  // API Key Required Modal state - start with this hidden until we've confirmed it's needed
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  
  // Tour state
  const [showTour, setShowTour] = useState(false);
  const [fileResetFlag, setFileResetFlag] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // New state to hide attachments preview during submission
  const [isSubmittingWithAttachments, setIsSubmittingWithAttachments] = useState(false);

  // Add this function to toggle the reset flag
  const resetFileUploads = () => {
    setFileResetFlag(prevFlag => !prevFlag);
  };

  const messageContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const userPromptRef = useRef({});
  const observerRef = useRef(null);
  const loadingIndicatorRef = useRef(null);
  
  // Improved function to scroll to ensure loading indicator is visible
  const scrollToLoadingIndicator = () => {
    if (messageContainerRef.current) {
      // First, scroll to bottom to ensure the most recent content is visible
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
      
      // Then, if we have a loading indicator, make sure it's fully visible
      if (loadingIndicatorRef.current) {
        // Give a small delay to ensure DOM updates are complete
        setTimeout(() => {
          const container = messageContainerRef.current;
          const indicator = loadingIndicatorRef.current;
          
          // Get the positions
          const containerRect = container.getBoundingClientRect();
          const indicatorRect = indicator.getBoundingClientRect();
          
          // Check if the indicator is fully visible
          const isFullyVisible = 
            indicatorRect.top >= containerRect.top && 
            indicatorRect.bottom <= containerRect.bottom;
          
          // If not fully visible, adjust scroll to make it visible
          if (!isFullyVisible) {
            // Calculate how much more we need to scroll
            const additionalScroll = 
              indicatorRect.bottom - containerRect.bottom + 20; // Add extra 20px padding
              
            if (additionalScroll > 0) {
              container.scrollTop += additionalScroll;
            }
          }
        }, 50); // Small delay to ensure rendering is complete
      }
    }
  };
  
  // Scroll when loading state changes
  useEffect(() => {
    if (isLoading) {
      // Use our enhanced scroll function
      scrollToLoadingIndicator();
      
      // For prompts with attachments, add an additional scroll check after a longer delay
      // to handle any delayed rendering
      if (files.length > 0 || isSubmittingWithAttachments) {
        setTimeout(scrollToLoadingIndicator, 150);
      }
    }
  }, [isLoading, files.length, isSubmittingWithAttachments]);
  
  // Function to check API key availability
  const checkApiKeyAvailability = () => {
    // If still initializing, don't show API key modal
    if (isInitializing || !trialInitialized) {
      console.log("ChatDialog: Still initializing or trial not initialized, allowing operation to proceed");
      return true; // Allow operation to proceed until initialization is complete
    }
    
    // Now check if trial is active
    if (trialActive === true) {
      console.log("ChatDialog: Trial is confirmed active, allowing operation");
      return true;
    }
    
    // Check if user has API key
    const userApiKey = localStorage.getItem('anthropic_api_key') || 
      (localStorage.getItem('anthropic_api_key_encrypted') ? 
        decryptData(localStorage.getItem('anthropic_api_key_encrypted')) : '');
    
    // If no trial AND no user API key, show our modal
    if (!trialActive && !userApiKey) {
      console.log("ChatDialog: No trial and no API key, showing modal");
      setShowApiKeyModal(true);
      return false;
    }
    
    return true;
  };
  
  // Handler to open settings
  const handleOpenSettings = () => {
    setShowApiKeyModal(false);
    window.dispatchEvent(new CustomEvent('openSettings', { 
      detail: { reason: 'apiKeyRequired' } 
    }));
  };
  
  // Store tour completion in localStorage
  const handleTourClose = (completed) => {
    console.log("Tour closed, completed:", completed);
    
    try {
      if (completed) {
        // Mark tour as shown when completed
        localStorage.setItem('blue_comet_tour_shown', 'true');
        console.log("Saved tour completion to localStorage");
        
        // Verify it was saved correctly
        const verifyValue = localStorage.getItem('blue_comet_tour_shown');
        console.log("Verified localStorage value after save:", verifyValue);
      }
    } catch (err) {
      console.error("Error saving tour state to localStorage:", err);
    }
    
    setShowTour(false);
  };
  
  // Start the tour
  const startTour = () => {
    console.log("Starting tour manually");
    setShowTour(true);
  };
  
  // Improved tour initialization logic with forced debug
  useEffect(() => {
    // Force tour to show on first load - checking and logging localStorage state
    const hasSeenTour = localStorage.getItem('blue_comet_tour_shown');
    
    console.log("ChatDialog: Tour initialization");
    console.log("ChatDialog: localStorage 'blue_comet_tour_shown' value:", hasSeenTour);
    
    // For debugging in incognito mode, let's also check if localStorage works as expected
    try {
      localStorage.setItem('test_storage_works', 'yes');
      const testValue = localStorage.getItem('test_storage_works');
      console.log("ChatDialog: localStorage test value:", testValue);
      localStorage.removeItem('test_storage_works');
    } catch (err) {
      console.error("ChatDialog: localStorage test failed:", err);
    }
    
    if (!hasSeenTour) {
      console.log("ChatDialog: First-time user, will show tour shortly");
      // Delay the tour slightly to ensure UI is fully loaded
      const timer = setTimeout(() => {
        console.log("ChatDialog: Now showing tour");
        setShowTour(true);
      }, 1500);
      
      return () => {
        console.log("ChatDialog: Cleaning up tour timer");
        clearTimeout(timer);
      };
    } else {
      console.log("ChatDialog: Returning user, tour already shown");
    }
  }, []);

  // Ensure the tour shows for new users with a second initialization check
  useEffect(() => {
    // This is a secondary check that runs after component has fully mounted
    // to ensure tour visibility for new users
    if (!localStorage.getItem('blue_comet_tour_shown')) {
      console.log("ChatDialog: Secondary tour initialization - ensuring tour visibility");
      // Using a different delay to avoid collision with primary initialization
      const secondaryTimer = setTimeout(() => {
        console.log("ChatDialog: Secondary tour check - showing tour");
        setShowTour(true);
      }, 2500);
      
      return () => clearTimeout(secondaryTimer);
    }
  }, []);
  
  // Initialize free trial on mount
  useEffect(() => {
    console.log("ChatDialog: Initializing free trial...");
    
    // Track whether initialization has completed
    let isMounted = true;
    
    // Set initializing state
    setIsInitializing(true);
    
    // First check localStorage directly to ensure UI state is correct
    const storedTrial = localStorage.getItem('freeTrialData');
    if (storedTrial) {
      try {
        const parsedData = JSON.parse(storedTrial);
        if (parsedData.active && parsedData.remaining > 0) {
          console.log("ChatDialog: Trial active from localStorage check");
          if (isMounted) {
            setTrialActive(true);
            setTrialRemaining(parsedData.remaining);
          }
        }
      } catch (err) {
        console.error("ChatDialog: Error parsing trial data:", err);
      }
    }
    
    // Now run normal initialization
    FreeTrialManager.initTrial().then(active => {
      console.log("ChatDialog: Free trial active:", active);
      console.log("ChatDialog: Remaining prompts:", FreeTrialManager.getRemainingPromptCount());
      if (isMounted) {
        setTrialActive(active);
        setTrialRemaining(FreeTrialManager.getRemainingPromptCount());
        setTrialInitialized(true); // Mark initialization as complete
        setIsInitializing(false); // End initialization state
      }
    }).catch(err => {
      console.error("ChatDialog: Error initializing free trial:", err);
      if (isMounted) {
        setTrialInitialized(true); // Even on error, mark as initialized
        setIsInitializing(false); // End initialization state
      }
    });
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Update trial count in state
  const updateTrialCount = () => {
    try {
      const remaining = FreeTrialManager.getRemainingPromptCount();
      setTrialRemaining(remaining);
      setTrialActive(FreeTrialManager.isTrialActive());
      console.log("ChatDialog: Updated trial count:", remaining, "active:", FreeTrialManager.isTrialActive());
    } catch (err) {
      console.error("ChatDialog: Error updating trial count:", err);
    }
  };

  // Get API key for request (from trial or user-provided)
  const getApiKeyForRequest = () => {
    if (trialActive === true) {
      const trialKey = FreeTrialManager.getTrialApiKey();
      console.log("ChatDialog: Using trial API key:", !!trialKey);
      return trialKey;
    } else {
      // Get user-provided key
      const userKey = localStorage.getItem('anthropic_api_key') || 
        (localStorage.getItem('anthropic_api_key_encrypted') ? 
          decryptData(localStorage.getItem('anthropic_api_key_encrypted')) : '');
      console.log("ChatDialog: Using user API key:", !!userKey);
      return userKey;
    }
  };

  // Load saved message from ref if available
  useEffect(() => {
    if (messageInputRef && messageInputRef.current && messageInputRef.current !== '') {
      setMessage(messageInputRef.current);
      // Place cursor at the end of the text after restoring
      if (textareaRef.current) {
        setTimeout(() => {
          const length = messageInputRef.current.length;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(length, length);
        }, 50);
      }
      messageInputRef.current = '';
    }
  }, [messageInputRef]);

  // Enhanced scrolling effect that handles continuations intelligently
  useEffect(() => {
    if (!messageContainerRef.current) return;
    
    // If continuation is in progress, check if user is at the bottom
    if (topic?.messages?.some(msg => msg.continuationInProgress)) {
      const container = messageContainerRef.current;
      const isAtBottom = 
        Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) < 100;
      
      // If user is looking at the bottom, auto-scroll to follow the continuation
      if (isAtBottom) {
        container.scrollTop = container.scrollHeight;
      }
    } else {
      // If no continuation in progress, use standard scroll behavior
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  }, [topic?.messages]);

  // Add CSS for the loading indicator and code blocks
  useEffect(() => {
    // Add the required styles to the document
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      .initial-loading-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: rgba(255, 255, 255, 0.9);
        z-index: 10;
      }
      
      .loading-indicator {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 20px;
        min-height: 60px;
        width: 100%;
        margin-top: 12px;
      }
      
      .messages-container {
        padding-bottom: 30px;
        scroll-behavior: smooth;
      }
      
      /* Code block styling */
      .code-block-wrapper {
        margin: 16px 0;
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid #2d2d2d;
        background: #1e1e1e;
        box-shadow: var(--shadow-sm);
      }
      
      .code-container {
        position: relative;
        overflow: hidden;
        max-width: 100%;
        overflow-x: auto;
      }
      
      .code-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 16px;
        background: #2d2d2d !important;
        border-bottom: 1px solid #3d3d3d;
        color: #e0e0e0 !important;
      }
      
      .code-language {
        color: #b9b9b9 !important;
        font-size: 13px;
        font-family: monospace;
        text-transform: lowercase;
      }
      
      .code-actions {
        display: flex;
        gap: 8px;
        align-items: center;
      }
      
      pre.syntax-highlighter {
        margin: 0 !important;
        padding: 16px !important;
        background: #1e1e1e !important;
        border-radius: 0 !important;
        font-size: 14px !important;
        line-height: 1.5 !important;
        overflow-x: auto !important;
      }
      
      .syntax-highlighter-code {
        font-family: "Fira Code", Consolas, Monaco, "Courier New", monospace !important;
      }
    `;
    document.head.appendChild(styleEl);
    
    return () => {
      // Clean up when component unmounts
      document.head.removeChild(styleEl);
    };
  }, []);
  
  // Auto-resize textarea as content grows
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(200, textareaRef.current.scrollHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [message]);

  // Add a fadeout effect for error messages
  useEffect(() => {
    if (error) {
      // Set timer to clear error after 5 seconds
      const timer = setTimeout(() => {
        const fadeOutError = () => {
          // First add a fade-out class
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.classList.add('fade-out');
          }
          
          // Then after the animation is complete, remove the error
          setTimeout(() => {
            setError(null);
          }, 300); // Animation duration in ms
        };
        
        fadeOutError();
      }, 4700); // 5000ms - 300ms animation duration
      
      return () => clearTimeout(timer); // Cleanup timeout
    }
  }, [error]);

  // Store the user's prompts for filename generation
  useEffect(() => {
    if (topic && topic.messages) {
      // Build a map of message pairs (user prompt → assistant response with code)
      const prompts = {};
      
      for (let i = 0; i < topic.messages.length - 1; i++) {
        if (topic.messages[i].role === 'user' && topic.messages[i+1].role === 'assistant') {
          // Store user message content indexed by the position of the assistant's response
          prompts[i+1] = topic.messages[i].content;
        }
      }
      
      userPromptRef.current = prompts;
    }
  }, [topic]);

  // Modified setupCodeBlockCopyButtons function for continuation support, with line number fix
  const setupCodeBlockCopyButtons = () => {
    // Function to set up the buttons for a single code block
    const setupCodeBlockButtons = (wrapper) => {
      const block = wrapper.querySelector('pre');
      const copyButton = wrapper.querySelector('.copy-code-button');
      const saveButton = wrapper.querySelector('.save-code-button');
      
      // Store original code content in a data attribute if not already stored
      if (block && !block.hasAttribute('data-original-code') && !block.dataset.originalCode) {
        // Try to get the original code from the content
        const originalCode = extractCleanCode(block);
        block.setAttribute('data-original-code', originalCode);
      }
      
      // Set up copy button functionality if it hasn't been initialized
      if (copyButton && block && !copyButton.hasAttribute('data-initialized')) {
        copyButton.setAttribute('data-initialized', 'true');
        
        copyButton.onclick = (e) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          
          // Get the clean code without line numbers
          const code = block.getAttribute('data-original-code') || extractCleanCode(block);
          
          navigator.clipboard.writeText(code)
            .then(() => {
              copyButton.textContent = 'Copied!';
              copyButton.classList.add('copied');
              setTimeout(() => {
                copyButton.textContent = 'Copy';
                copyButton.classList.remove('copied');
              }, 2000);
            })
            .catch(err => {
              console.error('Failed to copy code: ', err);
            });
        };
      }
      
      // Set up save button functionality if it hasn't been initialized
      if (saveButton && block && !saveButton.hasAttribute('data-initialized')) {
        saveButton.setAttribute('data-initialized', 'true');
        
        saveButton.onclick = async (e) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          
          // Find which message contains this code block
          const messages = document.querySelectorAll('.message');
          let messageIndex = null;
          
          for (let i = 0; i < messages.length; i++) {
            if (messages[i].contains(wrapper)) {
              messageIndex = i;
              break;
            }
          }
          
          // Get the language from the code block header
          const codeLanguageElement = wrapper.querySelector('.code-language');
          const language = codeLanguageElement ? 
            codeLanguageElement.textContent.trim().toLowerCase() : 'text';
          
          // Get the user prompt that generated this code
          const promptContext = messageIndex !== null ? 
            userPromptRef.current[messageIndex] || '' : '';
          
          // Save code to file
          setIsSavingCode(true);
          saveButton.textContent = 'Saving...';
          
          try {
            // Get the clean code without line numbers
            const code = block.getAttribute('data-original-code') || extractCleanCode(block);
            
            await saveCodeToFile(code, language, promptContext);
            
            saveButton.textContent = 'Saved!';
            setTimeout(() => {
              saveButton.textContent = 'Save';
            }, 2000);
          } catch (error) {
            console.error('Error saving code:', error);
            saveButton.textContent = 'Error';
            setTimeout(() => {
              saveButton.textContent = 'Save';
            }, 2000);
          } finally {
            setIsSavingCode(false);
          }
        };
      }
    };
    
    // Create a MutationObserver to detect new code blocks being added to the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        // Check for added nodes
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            // Look for code blocks within the added DOM
            const newWrappers = node.querySelectorAll ? 
              node.querySelectorAll('.code-block-wrapper') : [];
            
            // Process any code blocks found in the new content
            if (newWrappers.length > 0) {
              Array.from(newWrappers).forEach(setupCodeBlockButtons);
            }
            
            // Also check if node itself is a code block wrapper
            if (node.classList && node.classList.contains('code-block-wrapper')) {
              setupCodeBlockButtons(node);
            }
          }
        });
      });
    });
    
    // Store observer in ref for cleanup
    observerRef.current = observer;
    
    // Start observing the DOM for changes
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
    
    // Initial setup for any existing code blocks
    document.querySelectorAll('.code-block-wrapper').forEach(setupCodeBlockButtons);
    
    // Cleanup function to disconnect the observer when component unmounts
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  };

  // Call setupCodeBlockCopyButtons when topic.messages change
  useEffect(() => {
    console.log("Setting up code block buttons");
    // Initialize code block copy buttons
    const cleanup = setupCodeBlockCopyButtons();
    
    // Scroll to bottom for fresh messages
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
    
    // Focus textarea when component mounts
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
    
    return cleanup;
  }, [topic?.messages]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if ((!message.trim() && !files.length) || isLoading || isSubmitting) return;
    
    // If we're still initializing, don't show API key modal
    if (isInitializing) {
      console.log("ChatDialog: Still initializing, delaying submission");
      setError("Loading app configuration. Please try again in a moment.");
      return;
    }
    
    // Check for API key before proceeding
    if (!checkApiKeyAvailability()) {
      return;
    }
    
    const currentMessage = message.trim();
    
    // Create a proper deep copy of files to ensure we preserve ALL data
    const currentFiles = files.map(file => {
      // Create a new file object with all properties
      const fileCopy = { ...file };
      
      // Ensure binary data is properly preserved
      if (file.buffer) fileCopy.buffer = file.buffer;
      if (file.dataUrl) fileCopy.dataUrl = file.dataUrl;
      if (file.content) fileCopy.content = file.content;
      
      // Make sure we have all the metadata
      if (file.text) fileCopy.text = file.text;
      if (file.html) fileCopy.html = file.html;
      if (file.parsed) fileCopy.parsed = file.parsed;
      
      return fileCopy;
    });
    
    // Create cleaned file metadata for storage
    const cleanedFiles = cleanFilesForStorage(currentFiles);
    
    const apiKey = getApiKeyForRequest();
    
    // Set submitting state to prevent double submissions
    setIsSubmitting(true);
    
    // Clear the message input immediately for better UX
    setMessage('');
    
    // Hide attachment preview during submission if we have files
    if (files.length > 0) {
      setIsSubmittingWithAttachments(true);
    }
    
    // Close the file upload area immediately
    setShowFileUpload(false);
    
    console.log("Preparing files for API submission:", currentFiles.length, "files");
    
    try {
      // Process PDF files if present
      const hasPdfFiles = needsPdfBetaHeader(currentFiles);
      let messageContent = [];
      
      // If we have PDF files, prepare them for the API
      if (hasPdfFiles) {
        console.log("PDF files detected, preparing for Anthropic API");
        
        // Prepare file content blocks for the API
        const fileContentBlocks = await prepareFilesForAnthropicApi(currentFiles);
        
        // Add file content blocks
        messageContent = [...fileContentBlocks];
        
        // Add the text prompt
        if (currentMessage) {
          messageContent.push({
            type: "text",
            text: currentMessage
          });
        }
        
        console.log(`Prepared ${fileContentBlocks.length} files for API submission`);
      }
      
      // Make sure we perform an immediate scroll, especially important for attachment submissions
      if (messageContainerRef.current) {
        messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
        
        // Add a small additional scroll to ensure spinner will be visible
        const extraScroll = 100; // Pixels of extra space to scroll down
        setTimeout(() => {
          messageContainerRef.current.scrollTop += extraScroll;
        }, 50);
      }
      
      // Call the API with the proper content structure
      onMessageSubmit({
        content: currentMessage,     // Just the message text without files
        files: currentFiles,         // Keep full files for API submission
        cleanedFiles: cleanedFiles,  // Add cleaned files for storage
        isEnhancementRequest: false,
        apiKey,
        hasPdfFiles: hasPdfFiles,
        messageContent: messageContent
      })
        .then(() => {
          // API call successful!
          console.log("API call successful, resetting UI state");
          
          // Reset files since we're done with them
          setFiles([]);
          
          // Reset the submitting with attachments flag
          setIsSubmittingWithAttachments(false);
          
          // Reset file uploads
          resetFileUploads();
          
          // Handle trial usage recording
          if (trialActive) {
            FreeTrialManager.recordSuccessfulPrompt({}, true).then((remaining) => {
              console.log("ChatDialog: Recorded prompt usage, remaining:", remaining);
              
              // Get the updated trial data directly from localStorage to ensure sync
              const storedTrial = localStorage.getItem('freeTrialData');
              if (storedTrial) {
                try {
                  const parsedData = JSON.parse(storedTrial);
                  setTrialActive(parsedData.active);
                  setTrialRemaining(parsedData.remaining);
                  console.log("ChatDialog: Updated trial state from localStorage - active:", parsedData.active, "remaining:", parsedData.remaining);
                  // Dispatch trial updated event
                  window.dispatchEvent(new Event('trialUpdated'));
                } catch (err) {
                  console.error("ChatDialog: Error parsing trial data after prompt:", err);
                  updateTrialCount();
                  // Dispatch trial updated event
                  window.dispatchEvent(new Event('trialUpdated'));
                }
              } else {
                console.log("ChatDialog: No stored trial data found, using updateTrialCount()");
                updateTrialCount();
                // Dispatch trial updated event
                window.dispatchEvent(new Event('trialUpdated'));
              }
            }).catch(err => {
              console.error("ChatDialog: Error recording prompt usage:", err);
              updateTrialCount();
              // Dispatch trial updated event
              window.dispatchEvent(new Event('trialUpdated'));
            });
          } else {
            console.log("ChatDialog: Trial not active, skipping usage recording");
          }
        })
        .catch((err) => {
          // API call failed - restore UI state
          console.error("ChatDialog: Error during message submission:", err);
          
          // Display error message
          setError("The AI service is busy. Please try again in a few moments.");
          
          // Restore the message input
          setMessage(currentMessage);
          
          // Restore the files
          setFiles(currentFiles);
          
          // Show the attachment preview again since we need to retry
          setIsSubmittingWithAttachments(false);
          
          // Backup the message
          messageInputRef.current = currentMessage;
          
          // Handle error fadeout
          setTimeout(() => {
            const fadeOutError = () => {
              // First add a fade-out class
              const errorElement = document.querySelector('.error-message');
              if (errorElement) {
                errorElement.classList.add('fade-out');
              }
              
              // Then after the animation is complete, remove the error
              setTimeout(() => {
                setError(null);
              }, 300); // Animation duration in ms
            };
            
            fadeOutError();
          }, 4700); // 5000ms - 300ms animation duration
        })
        .finally(() => {
          // Reset submitting state
          setIsSubmitting(false);
        });
    } catch (error) {
      // Handle file processing errors
      console.error("Error processing files:", error);
      setError("Error processing files. Please try again.");
      setMessage(currentMessage);
      setFiles(currentFiles);
      messageInputRef.current = currentMessage;
      setIsSubmitting(false);
      
      // Show the attachment preview again since we need to retry
      setIsSubmittingWithAttachments(false);
      
      // Error fadeout timer
      setTimeout(() => {
        const fadeOutError = () => {
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.classList.add('fade-out');
          }
          setTimeout(() => {
            setError(null);
          }, 300);
        };
        fadeOutError();
      }, 4700);
    }
  };

  const handleEnhanceClick = async () => {
    if (!message.trim() || isLoading) return;
    
    // Check for API key before proceeding
    if (!checkApiKeyAvailability()) {
      return;
    }
    
    // Add processing class to enhance button for visual feedback
    const enhanceButton = document.querySelector('.enhance-button');
    if (enhanceButton) {
      enhanceButton.classList.add('processing');
    }
    
    setIsEnhancing(true);
    
    try {
      // Get previous messages for context
      const previousMessages = topic && topic.messages ? [...topic.messages] : [];
      
      // Create a context-aware enhancement prompt using the utility function
      const enhancementPrompt = generateEnhancementPrompt(message.trim(), previousMessages);
      
      // Get API key (use trial key if available)
      const apiKey = getApiKeyForRequest();
      
      if (!apiKey) {
        throw new Error("API key is required for prompt enhancement");
      }
      
      // Create a temporary client
      const client = new Anthropic({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true,
      });
      
      // Show enhancement dialog immediately with loading state
      setShowEnhancementDialog(true);
      
      // Call the API - make sure this doesn't get added to the conversation
      const response = await client.messages.create({
        model: localStorage.getItem('anthropic_model') || 'claude-3-7-sonnet-20250219',
        max_tokens: 1024,
        messages: [{
          role: 'user',
          content: enhancementPrompt
        }],
        temperature: 0.5
      });
      
      // Set the enhanced prompt from the API response
      if (response && response.content && response.content[0]?.text) {
        setEnhancedPrompt(response.content[0].text);
        
        // If trial was used, record the prompt
        if (trialActive) {
          FreeTrialManager.recordSuccessfulPrompt().then(() => {
            // Update trial count after enhancement
            const storedTrial = localStorage.getItem('freeTrialData');
            if (storedTrial) {
              try {
                const parsedData = JSON.parse(storedTrial);
                setTrialActive(parsedData.active);
                setTrialRemaining(parsedData.remaining);
              } catch (err) {
                console.error("Error updating trial data after enhancement:", err);
                updateTrialCount();
              }
            } else {
              updateTrialCount();
            }
          });
        }
      } else {
        throw new Error("Failed to get enhanced prompt from Claude");
      }
    } catch (err) {
      console.error("Enhancement error:", err);
      // Check if this is an API busy error
      const isBusyError = err.message?.includes("busy") || 
                        err.status === 429 || 
                        err.message?.includes("rate limit") ||
                        err.message?.includes("too many requests");
      
      if (isBusyError) {
        setError("The AI service is currently busy. Please try again in a few moments.");
      } else {
        setError("Failed to enhance prompt. Please try again.");
      }
      
      // Close dialog if there was an error
      setShowEnhancementDialog(false);
    } finally {
      setIsEnhancing(false);
      // Remove processing class from button
      const enhanceButton = document.querySelector('.enhance-button');
      if (enhanceButton) {
        enhanceButton.classList.remove('processing');
      }
    }
  };
  
  const handleAcceptEnhancement = (selectedPrompt) => {
    // Set the selected prompt as the message
    setMessage(selectedPrompt);
    setShowEnhancementDialog(false);
    
    // Focus the textarea and put cursor at the end
    if (textareaRef.current) {
      textareaRef.current.focus();
      const length = selectedPrompt.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  };
  
  const toggleFileUpload = () => {
    setShowFileUpload(!showFileUpload);
  };

  const handleFileUpload = (uploadedFiles) => {
    // Use the uploaded files directly, ensuring deep copy to avoid reference issues
    console.log("ChatDialog: Received files from FileUpload:", uploadedFiles.length);
    setFiles([...uploadedFiles]);
  };

  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    
    // Updated: Always hide upload area if there are no files
    if (newFiles.length === 0) {
      setShowFileUpload(false);
    }
  };

  // Highlighting search terms in messages
  const highlightSearchTerm = (text) => {
    if (!searchTerm || !text) return text;
    
    // Create a safe search term pattern - escape special regex characters
    const safeSearchTerm = searchTerm.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    
    // Only attempt to split and highlight if there's a valid search term
    try {
      const regex = new RegExp(`(${safeSearchTerm})`, 'gi');
      const parts = text.split(regex);
      
      return parts.map((part, i) => {
        return part.toLowerCase() === searchTerm?.toLowerCase() ? 
          `<span class="highlight-text">${part}</span>` : part;
      }).join('');
    } catch (err) {
      // Fallback if regex fails - just return the original text
      console.error("Search highlighting error:", err);
      return text;
    }
  };

  // Process user message content - no markdown rendering
  const processUserMessage = (content) => {
    if (!content) return '';
    // Apply search highlighting to plain text
    const highlighted = highlightSearchTerm(content);
    return highlighted;
  };

  // Component to highlight search terms in text nodes
  const HighlightedText = ({ text }) => {
    if (!searchTerm || !text) return <>{text}</>;
    
    // Create a safe search term pattern - escape special regex characters
    const safeSearchTerm = searchTerm.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    
    // Only attempt to split and highlight if there's a valid search term
    try {
      const regex = new RegExp(`(${safeSearchTerm})`, 'gi');
      const parts = text.split(regex);
      
      return (
        <>
          {parts.map((part, i) => 
            part.toLowerCase() === searchTerm?.toLowerCase() ? 
              <span key={i} className="highlight-text">{part}</span> : 
              <span key={i}>{part}</span>
          )}
        </>
      );
    } catch (err) {
      // Fallback if regex fails - just return the original text
      console.error("Search highlighting error:", err);
      return <>{text}</>;
    }
  };

  // Custom renderer for code blocks in markdown with line number fix
  const renderers = {
    code: ({node, inline, className, children, ...props}) => {
      const match = /language-(\w+)/.exec(className || '');
      const codeContent = String(children).replace(/\n$/, '');
      
      if (!inline && match) {
        // Detect language from className
        const lang = match[1].toLowerCase();
        
        // Determine language display name (more user-friendly)
        let displayLang = lang;
        switch(lang) {
          case 'js':
            displayLang = 'JavaScript';
            break;
          case 'ts':
            displayLang = 'TypeScript';
            break;
          case 'jsx':
            displayLang = 'React JSX';
            break;
          case 'tsx':
            displayLang = 'React TSX';
            break;
          case 'py':
            displayLang = 'Python';
            break;
          case 'rb':
            displayLang = 'Ruby';
            break;
          case 'cpp':
            displayLang = 'C++';
            break;
          case 'cs':
            displayLang = 'C#';
            break;
          case 'html':
            displayLang = 'HTML';
            break;
          case 'htm':
            displayLang = 'HTML';
            break;
          // Add more language mappings as needed
          default:
            // Capitalize first letter
            displayLang = lang.charAt(0).toUpperCase() + lang.slice(1);
        }
        
        return (
          <div className="code-block-wrapper">
            <div className="code-header">
              <div className="code-language">{displayLang}</div>
              <div className="code-actions">
                <button className="code-action-button copy-code-button">Copy</button>
                <button className="code-action-button save-code-button">
                  <SaveIcon /> Save
                </button>
              </div>
            </div>
            <div className="code-container">
              {searchTerm ? (
                // When search term exists, we need custom rendering with highlighting
                <pre className="syntax-highlighter" data-original-code={codeContent}>
                  <code className="syntax-highlighter-code">
                    {(() => {
                      // Self-executing function to allow for regular JavaScript logic in JSX
                      // Create a safe search term pattern - escape special regex characters
                      const safeSearchTerm = searchTerm.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
                      // Only attempt to split and highlight if there's a valid search term
                      try {
                        const regex = new RegExp(`(${safeSearchTerm})`, 'gi');
                        const parts = codeContent.split(regex);
                        return parts.map((part, i) => 
                          part.toLowerCase() === searchTerm.toLowerCase() 
                            ? <span key={i} className="highlight-text">{part}</span> 
                            : <span key={i}>{part}</span>
                        );
                      } catch (err) {
                        // Fallback if regex fails - just return the code without highlighting
                        console.error("Search highlighting error:", err);
                        return codeContent;
                      }
                    })()}
                  </code>
                </pre>
              ) : (
                // Normal syntax highlighting when no search term - with data-original-code to store clean version
                <SyntaxHighlighter
                  style={customCodeStyle}
                  language={lang}
                  className="syntax-highlighter"
                  PreTag={(preProps) => <pre {...preProps} data-original-code={codeContent} />}
                  wrapLongLines={true}
                  showLineNumbers={true}
                  {...props}
                >
                  {codeContent}
                </SyntaxHighlighter>
              )}
            </div>
          </div>
        );
      }
      // Inline code handling
      if (searchTerm) {
        // Self-executing function to allow for regular JavaScript logic
        return (
          <code className={className} {...props}>
            {(() => {
              // Create a safe search term pattern - escape special regex characters
              const safeSearchTerm = searchTerm.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
              // Only attempt to split and highlight if there's a valid search term
              try {
                const regex = new RegExp(`(${safeSearchTerm})`, 'gi');
                const parts = codeContent.split(regex);
                return parts.map((part, i) => 
                  part.toLowerCase() === searchTerm.toLowerCase() 
                    ? <span key={i} className="highlight-text">{part}</span> 
                    : <span key={i}>{part}</span>
                );
              } catch (err) {
                // Fallback if regex fails - just return the code without highlighting
                console.error("Search highlighting error:", err);
                return codeContent;
              }
            })()}
          </code>
        );
      }
      
      // Default rendering for inline code without search
      return <code className={className} {...props}>{children}</code>;
    },
    // Custom renderers for paragraphs, lists, and headings with search highlighting
    p: ({node, children, ...props}) => {
      if (!searchTerm) {
        return <p {...props}>{children}</p>;
      }
      
      const enhancedChildren = React.Children.map(children, child => {
        if (typeof child === 'string') {
          return <HighlightedText text={child} />;
        }
        return child;
      });
      
      return <p {...props}>{enhancedChildren}</p>;
    },
    // Other custom renderers (li, h1, h2, h3) for search highlighting
    li: ({node, children, ...props}) => {
      if (!searchTerm) {
        return <li {...props}>{children}</li>;
      }
      
      const enhancedChildren = React.Children.map(children, child => {
        if (typeof child === 'string') {
          return <HighlightedText text={child} />;
        }
        return child;
      });
      
      return <li {...props}>{enhancedChildren}</li>;
    },
    
    h1: ({node, children, ...props}) => {
      if (!searchTerm) {
        return <h1 {...props}>{children}</h1>;
      }
      
      const enhancedChildren = React.Children.map(children, child => {
        if (typeof child === 'string') {
          return <HighlightedText text={child} />;
        }
        return child;
      });
      
      return <h1 {...props}>{enhancedChildren}</h1>;
    },
    
    h2: ({node, children, ...props}) => {
      if (!searchTerm) {
        return <h2 {...props}>{children}</h2>;
      }
      
      const enhancedChildren = React.Children.map(children, child => {
        if (typeof child === 'string') {
          return <HighlightedText text={child} />;
        }
        return child;
      });
      
      return <h2 {...props}>{enhancedChildren}</h2>;
    },
    
    h3: ({node, children, ...props}) => {
      if (!searchTerm) {
        return <h3 {...props}>{children}</h3>;
      }
      
      const enhancedChildren = React.Children.map(children, child => {
        if (typeof child === 'string') {
          return <HighlightedText text={child} />;
        }
        return child;
      });
      
      return <h3 {...props}>{enhancedChildren}</h3>;
    }
  };

  // Handle copy message content with safe event handling
  const handleCopyMessage = (content, index, e) => {
    // Add safe event handling
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    
    navigator.clipboard.writeText(content)
      .then(() => {
        const button = document.querySelectorAll('.copy-message-button')[index];
        if (button) {
          button.classList.add('copied');
          button.innerText = 'Copied!';
          setTimeout(() => {
            button.classList.remove('copied');
            button.innerText = 'Copy';
          }, 2000);
        }
      })
      .catch(err => {
        console.error('Failed to copy message:', err);
      });
  };
  
  // Handle saving of current prompt to the library
  const handleSaveCurrentPrompt = () => {
    if (!message.trim()) return;
    setPromptToSave(message.trim());
    setShowSavePromptModal(true);
  };
  
  return (
    <>
      {/* Show a loading indicator during initial setup */}
      {isInitializing && (
        <div className="initial-loading-container">
          <Spinner size="medium" color="#0078ff" label="Loading app configuration..." />
        </div>
      )}
      
      <div 
        className="messages-container" 
        ref={messageContainerRef}
      >
        {topic && topic.messages && topic.messages.map((msg, index) => (
          <div 
            key={index} 
            className={`message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
          >
            <div className="message-header">
              <span className="message-role">
                {msg.role === 'user' ? 'You' : 'Claude'}
              </span>
              <div className="message-actions">
                {msg.role === 'user' && (
                  <PromptLibraryButton
                    onClick={(e) => {
                      // Safe event handling
                      if (e && typeof e.preventDefault === 'function') {
                        e.preventDefault();
                      }
                      setPromptToSave(msg.content);
                      setShowSavePromptModal(true);
                    }}
                  />
                )}
                <button 
                  className="copy-message-button"
                  onClick={(e) => handleCopyMessage(msg.content, index, e)}
                >
                  Copy
                </button>
              </div>
            </div>
            
            {msg.role === 'user' ? (
              <div 
                className="user-message-content"
                dangerouslySetInnerHTML={{ 
                  __html: processUserMessage(msg.content) 
                }}
              />
            ) : (
              <div className="message-content">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={renderers}
                >
                  {msg.content}
                </ReactMarkdown>
                
                {/* Show continuation indicator if in progress */}
                {msg.continuationInProgress && (
                  <ContinuationIndicator />
                )}
              </div>
            )}
            
            {/* Improved file attachments display */}
            {msg.files && msg.files.length > 0 && (
              <MessageFileAttachments 
                files={msg.files} 
                threadId={topic.id}
                role={msg.role} 
              />
            )}
          </div>
        ))}
        
        {isLoading && (
          <div className="loading-indicator" ref={loadingIndicatorRef}>
            <Spinner size="small" color="#0078ff" label="Thinking..." />
          </div>
        )}
      </div>
      
      {topic && (
        <SummaryIcon topic={topic} />
      )}
      
      {/* Add TrialStatus component */}
      <TrialStatus />
      
      <div className="input-container">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        {/* File Upload with new close button functionality */}
        {showFileUpload && (
          <FileUpload
            onFilesAdded={handleFileUpload}
            onError={setError}
            existingFiles={files}
            resetFlag={fileResetFlag}
            onClose={toggleFileUpload} // Add the close handler
          />
        )}
        
        {/* SIMPLIFIED: Only show attachment preview when not submitting */}
        {files.length > 0 && !showFileUpload && !isSubmittingWithAttachments && (
          <AttachedFilesPreview 
            files={files}
            onRemoveFile={removeFile}
            onToggleFileUpload={toggleFileUpload}
          />
        )}
        
        <form className="message-form" onSubmit={(e) => { 
          if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
          }
          handleSubmit(); 
        }}>
          {/* Attach Button with Tooltip */}
          <div className="tooltip-container">
            <button
              className={`attach-button ${showFileUpload ? 'active' : ''}`}
              onClick={(e) => { 
                if (e && typeof e.preventDefault === 'function') {
                  e.preventDefault();
                }
                toggleFileUpload(); 
              }}
              aria-label="Attach files"
              type="button"
            >
              <span>📎</span>
              <span className="tooltip">Attach files</span>
            </button>
          </div>
          
          <div className="textarea-container">
            <textarea
              ref={textareaRef}
              className="message-textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Message Claude..."
              rows={1}
            />
          </div>
          
          {/* Save Current Prompt Button with Tooltip */}
          <div className="tooltip-container">
            <button
              className="save-current-prompt-button"
              onClick={(e) => {
                if (e && typeof e.preventDefault === 'function') {
                  e.preventDefault();
                }
                handleSaveCurrentPrompt();
              }}
              disabled={!message.trim()}
              aria-label="Save prompt to library"
              type="button"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="bookmark-icon"
              >
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
              </svg>
              <span className="tooltip">Save to Prompt Library</span>
            </button>
          </div>
          
          {/* Enhance Button with Tooltip */}
          <div className="tooltip-container">
            <button
              className="enhance-button"
              onClick={(e) => { 
                if (e && typeof e.preventDefault === 'function') {
                  e.preventDefault();
                }
                handleEnhanceClick(); 
              }}
              disabled={!message.trim() || isLoading || isEnhancing}
              aria-label="Enhance your prompt"
              type="button"
            >
              <MagicWand />
              <span className="tooltip">Enhance your prompt</span>
            </button>
          </div>
          
          {/* Library Button with Tooltip */}
          <div className="tooltip-container">
            <button
              className="library-button"
              onClick={(e) => { 
                if (e && typeof e.preventDefault === 'function') {
                  e.preventDefault();
                }
                setShowPromptLibraryModal(true); 
              }}
              aria-label="Open Prompt Library"
              type="button"
            >
              <LibraryIcon />
              <span className="tooltip">Prompt Library</span>
            </button>
          </div>
          
          {/* Send Button with Tooltip */}
          <div className="tooltip-container">
            <button
              className="send-button"
              disabled={(!message.trim() && !files.length) || isLoading || isSubmitting}
              type="submit"
              aria-label="Send message"
            >
              Send
              <span className="tooltip">Send message (Enter)</span>
            </button>
          </div>
        </form>
        
        <div className="input-help-text">
          Press <kbd>Enter</kbd> to send, <kbd>Shift</kbd> + <kbd>Enter</kbd> for new line
        </div>
      </div>
      
      {/* Updated Prompt Enhancement Component */}
      <PromptEnhancement
        isOpen={showEnhancementDialog}
        onClose={() => setShowEnhancementDialog(false)}
        originalPrompt={message}
        enhancedPrompt={enhancedPrompt}
        onAccept={handleAcceptEnhancement}
        isLoading={isEnhancing}
      />
      
      {/* Save Prompt Modal */}
      <SavePromptModal
        isOpen={showSavePromptModal}
        onClose={(saved) => {
          setShowSavePromptModal(false);
          if (saved) {
            // Optional: Show success message
          }
        }}
        promptContent={promptToSave}
      />

      {/* Prompt Library Modal */}
      <PromptLibraryModal
        isOpen={showPromptLibraryModal}
        onClose={() => setShowPromptLibraryModal(false)}
        onSelectPrompt={(promptContent) => {
          setMessage(promptContent);
          // Focus and place cursor at end
          if (textareaRef.current) {
            textareaRef.current.focus();
            const length = promptContent.length;
            textareaRef.current.setSelectionRange(length, length);
          }
        }}
      />
      
      {/* API Key Required Modal */}
      <ApiKeyRequiredModal 
        isOpen={showApiKeyModal}
        onClose={() => setShowApiKeyModal(false)}
        onOpenSettings={handleOpenSettings}
      />
      
      {/* Tour Component with improved handling */}
      <ChatTour 
        isOpen={showTour}
        onClose={handleTourClose}
      />
      
      {/* Use the new CustomHelpButton component */}
      <CustomHelpButton onClick={startTour} />
    </>
  );
}

export default ChatDialog;